import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton } from '@wix/design-system';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import { TemplateGalleryCard } from '@common/types';
import TemplateCardAdaptive from '@components/Templates/TemplateGallery/TemplateCard/TemplateCardAdaptive';
import { TemplatesContextProvider } from '@components/Contexts/TemplatesContext';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import {
  getDataCapsule,
  RECOMMENDED_TEMPLATE_ENTRY_KEY,
} from '../data-capsule-adaptor';

import s from './RecommendedTemplatesCarousel.module.scss';

export type RecommendedSectionType = 'you_may_also_like' | 'more_by_partner';

const SECTION_LABELS: Record<RecommendedSectionType, string> = {
  you_may_also_like: 'Templates you may also like',
  more_by_partner: 'More templates by',
};

export const SECTION_ENTRIES: Record<RecommendedSectionType, string> = {
  you_may_also_like: 'templates you may like',
  more_by_partner: 'more templates by',
};

async function storeRecommendedEntry(
  entryValue: string,
  errorMonitor?: { captureException(exception: Error): void },
) {
  try {
    const capsule = getDataCapsule();
    await capsule.setItem(RECOMMENDED_TEMPLATE_ENTRY_KEY, entryValue);
  } catch (err) {
    errorMonitor?.captureException(err as Error);
  }
}

interface Props {
  templates: TemplateGalleryCard[];
  isMobileView?: boolean;
  sectionType: RecommendedSectionType;
  getActionButton?: (template: TemplateGalleryCard) => React.ReactNode;
}

const DESKTOP_CARD_WIDTH = 278;
const DESKTOP_CARD_GAP = 24;

const MOBILE_CARD_WIDTH = 311;
const MOBILE_CARD_GAP = 18;

const MIN_TEMPLATES_FOR_SLIDER = 4;

const RecommendedTemplatesCarousel: React.FC<Props> = ({
  templates,
  isMobileView = false,
  sectionType,
  getActionButton,
}) => {
  const cardWidth = isMobileView ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH;
  const cardGap = isMobileView ? MOBILE_CARD_GAP : DESKTOP_CARD_GAP;
  const useSlider =
    isMobileView || templates.length >= MIN_TEMPLATES_FOR_SLIDER;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const errorMonitor = useErrorMonitor();
  const { logBiClick } = useTemplateBi({
    is_mobile: isMobileView,
  });

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const scrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current =
        node;

      if (node) {
        resizeObserverRef.current = new ResizeObserver(updateScrollButtons);
        resizeObserverRef.current.observe(node);
      }
    },
    [updateScrollButtons],
  );

  const handleNext = () => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
    const scrollAmount = Math.min(cardWidth + cardGap, remaining);
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = Math.min(cardWidth + cardGap, el.scrollLeft);
    el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  const showPrevButton = !isMobileView && canScrollLeft;
  const showNextButton = !isMobileView && canScrollRight;

  const cards = templates.map((template, index) => {
    const isLast = index === templates.length - 1;
    return (
      <div
        key={template.id}
        className={isMobileView ? s.mobileSlide : s.slide}
        style={{
          width: cardWidth,
          marginRight: isLast ? 0 : cardGap,
          paddingRight: isLast && isMobileView ? 24 : 0,
        }}
      >
        <div
          className={isMobileView ? s.mobileCardInner : s.desktopCardInner}
          style={{ width: cardWidth }}
        >
          <TemplateCardAdaptive
            onCustomClick={() => {
              storeRecommendedEntry(SECTION_ENTRIES[sectionType], errorMonitor);
              logBiClick({
                clickType: 'Use template',
                selected_template_id: template.id ?? undefined,
                selected_template_owner_account_id:
                  template.accountId ?? undefined,
                recommended_template_section: SECTION_LABELS[sectionType],
              });
            }}
            template={template}
            index={index}
            forceRender
            isSimpleMobileView={isMobileView}
            actionButton={getActionButton?.(template)}
            actionButtonVisibility="hover"
          />
        </div>
      </div>
    );
  });

  return (
    <TemplatesContextProvider
      numberOfResults={0}
      numberOfPages={0}
      currentPage={1}
      sort="purchases"
    >
      <div
        className={s.carouselWrapper}
        data-fade-left={!isMobileView && canScrollLeft}
        data-fade-right={!isMobileView && canScrollRight}
      >
        {useSlider ? (
          <div
            ref={scrollContainerRef}
            className={
              isMobileView ? s.mobileScrollContainer : s.scrollContainer
            }
            onScroll={updateScrollButtons}
          >
            {cards}
          </div>
        ) : (
          <div className={s.staticGrid}>{cards}</div>
        )}

        {showPrevButton && (
          <Box
            position="absolute"
            left="12px"
            top="142px"
            className={s.navButton}
          >
            <IconButton onClick={handlePrevious} skin="dark" size="large">
              <ChevronLeft />
            </IconButton>
          </Box>
        )}

        {showNextButton && (
          <Box
            position="absolute"
            right="12px"
            top="142px"
            className={s.navButton}
          >
            <IconButton onClick={handleNext} skin="dark" size="large">
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </div>
    </TemplatesContextProvider>
  );
};

export default RecommendedTemplatesCarousel;
